/**
 * 持久化的失败分类与中文可读化。
 *
 * 这一层存在的理由，是"保存失败"在浏览器里**本来就是常态**：站点配额会被用完、隐私模式下
 * IndexedDB 直接不可用、另一个标签页正占着数据库。它们不是程序缺陷，而是必须被翻译成
 * 用户看得懂、并知道下一步做什么的提示。
 *
 * 因此定下两条纪律：
 *
 * 1. **可预期的环境问题用返回值表达，不用异常。** 配额满、存储不可用都通过 `StorageFailure`
 *    结构化返回。抛异常到界面会变成一堆未捕获的 rejection，而用户真正需要的那句话
 *    （"该清理历史记录了"）会被淹没在堆栈里；更糟的是调用方通常只 `catch {}` 一下，
 *    于是保存失败变成了静默失败 —— 这是持久化里最坏的结果。
 * 2. **异常只留给编程错误。** 写入不可序列化的值（函数、类实例）是缺陷，必须立刻炸出来，
 *    而不是等到读回来变成 `undefined`。
 *
 * 配额错误的形状各家浏览器并不一致，所以识别必须按"名字 + 数字码 + 消息"三重判定：
 * 只认 `name === 'QuotaExceededError'` 会在 Firefox（`NS_ERROR_DOM_QUOTA_REACHED`）
 * 或某些 WebView 上漏判，而漏判的表现就是"保存按钮点了没反应"。
 */

/** 持久化失败的分类。界面按它决定是提示、降级还是引导用户清理。 */
export type StorageFailureCode =
  /** 配额已满：存储能写，但空间不够 */
  | 'quota-exceeded'
  /** 存储整体不可用：隐私模式、站点数据被禁、环境里没有 IndexedDB */
  | 'unavailable'
  /** 数据库里已有比当前程序更新的版本，拒绝按旧格式解读 */
  | 'version-mismatch'
  /** 旧版本数据无法迁移到新版本 */
  | 'migration-failed'
  /** 值不可结构化克隆 */
  | 'not-serializable'
  /** 数据库被另一个标签页占用（版本升级被挡住） */
  | 'blocked'
  /** 其它：仍然要给出可读消息，而不是把原始异常丢给界面 */
  | 'unknown';

/** 一次保存失败的可读描述。`message` 给用户看，`advice` 给出下一步动作。 */
export interface StorageFailure {
  readonly code: StorageFailureCode;
  readonly message: string;
  readonly advice: string;
  /** 原始错误，仅供诊断面板/日志使用，界面不要直接渲染 */
  readonly cause?: unknown;
}

/** 保存结果：成功带值，失败带可读原因。上层不需要 try/catch 就能完整处理失败。 */
export type SaveOutcome<T> = SaveOk<T> | SaveFailure;

export interface SaveOk<T> {
  readonly ok: true;
  readonly value: T;
  /**
   * 写入确实成功了，但内容被别人改过（并发写）。
   * 策略是"最后写入胜出"，同时把覆盖行为如实回报 —— 既不阻塞用户，也不假装无事发生。
   */
  readonly conflict?: SaveConflict;
}

export interface SaveFailure {
  readonly ok: false;
  readonly failure: StorageFailure;
}

/** 两个写入者改同一条记录时的冲突信息 */
export interface SaveConflict {
  readonly expectedUpdatedAt: number | null;
  readonly actualUpdatedAt: number | null;
  /** 中文说明，可直接展示 */
  readonly message: string;
}

export interface SaveConflictOptions {
  readonly expectedUpdatedAt?: number | null;
  readonly actualUpdatedAt?: number | null;
}

/** 组装冲突信息，供各仓库复用 */
export function saveConflict(options: SaveConflictOptions = {}): SaveConflict {
  const expected = options.expectedUpdatedAt ?? null;
  const actual = options.actualUpdatedAt ?? null;
  return {
    expectedUpdatedAt: expected,
    actualUpdatedAt: actual,
    message:
      expected === null
        ? '这条记录在你打开页面之后被其它标签页写过，已按「最后写入胜出」保存并覆盖对方的内容。'
        : `这条记录已被其它标签页更新（你看到的是 ${expected}，实际已是 ${actual}），本次保存按「最后写入胜出」覆盖了对方的内容。`,
  };
}

/** 组装成功结果；`conflict` 未给时不会留下 `undefined` 字段（`exactOptionalPropertyTypes`） */
export function saveOk<T>(value: T, conflict?: SaveConflict): SaveOk<T> {
  return conflict === undefined ? { ok: true, value } : { ok: true, value, conflict };
}

/** 组装失败结果 */
export function saveFailure(failure: StorageFailure): SaveFailure {
  return { ok: false, failure };
}

/**
 * 跑一次保存，`work` 直接给出最终结果（成功值 + 可选的并发冲突）。
 *
 * 与 {@link attemptSave} 的差别在于"冲突"这件事：它只有在写入之后、对比了库里的时间戳才知道，
 * 因此必须由 `work` 自己决定。若把它塞进值里再让包装层翻译，翻译层很容易只认自己的
 * `conflict` 字段而把仓库的结论丢掉 —— 那会让"你的改动覆盖了别的标签页"这条提示静默消失。
 */
export async function runSave<T>(
  operation: string,
  work: () => Promise<SaveOk<T>>,
  context: { readonly collection?: string } = {},
): Promise<SaveOutcome<T>> {
  try {
    return await work();
  } catch (error) {
    return saveFailure(
      describeStorageFailure(
        error,
        context.collection === undefined ? { operation } : { operation, collection: context.collection },
      ),
    );
  }
}

/**
 * 跑一次"保存"动作并把任何异常翻译成可读失败。
 *
 * 每个仓库的保存方法都从这里走，于是"失败一律有中文原因 + 建议动作"这件事只有一个实现，
 * 不会出现某个仓库忘了 catch、把原始 DOMException 丢到界面上的情况。
 * 注意它**只**捕获，不吞掉：返回值里带着 `cause`，诊断面板仍然拿得到原始错误。
 */
export async function attemptSave<T>(
  operation: string,
  work: () => Promise<T>,
  context: { readonly collection?: string } = {},
): Promise<SaveOutcome<T>> {
  try {
    return saveOk(await work());
  } catch (error) {
    return saveFailure(
      describeStorageFailure(
        error,
        context.collection === undefined ? { operation } : { operation, collection: context.collection },
      ),
    );
  }
}

export interface PersistenceErrorOptions {
  readonly cause?: unknown;
}

/**
 * 持久化层的异常基类。
 *
 * 只用于"不该发生但发生了"的情况：值不可序列化、迁移失败、环境不提供存储。
 * 配额这类可预期的失败请走 `StorageFailure` 返回值。
 */
export class PersistenceError extends Error {
  readonly code: StorageFailureCode;
  readonly advice: string;

  constructor(
    code: StorageFailureCode,
    message: string,
    advice: string,
    options?: PersistenceErrorOptions,
  ) {
    super(message);
    this.name = 'PersistenceError';
    this.code = code;
    this.advice = advice;
    if (options?.cause !== undefined) this.cause = options.cause;
  }
}

/** 值不可结构化克隆：写进去读回来会变成别的形状，必须在写入前拒绝 */
export class NotStorableError extends PersistenceError {
  readonly path: string;

  constructor(path: string, reason: string) {
    super(
      'not-serializable',
      `值无法被持久化：${reason}（位置 ${path}）`,
      '这是调用方的缺陷：写入前请把它转换成可结构化克隆的普通数据（去掉函数、类实例与 Promise）。',
    );
    this.name = 'NotStorableError';
    this.path = path;
  }
}

/** 环境不提供可用的持久化存储（隐私模式、站点数据被禁、非浏览器环境） */
export class StorageUnavailableError extends PersistenceError {
  constructor(reason: string, options?: PersistenceErrorOptions) {
    super(
      'unavailable',
      `浏览器不允许使用持久化存储：${reason}`,
      '本次会话的改动只会留在内存里，刷新或关闭页面就会丢失。请退出隐私模式，或允许本站保存站点数据。',
      options,
    );
    this.name = 'StorageUnavailableError';
  }
}

/**
 * 数据库里的数据版本比当前程序更新（或旧数据无法迁移）。
 *
 * 注意这里**永远不通过删库来"修复"**：删库等于把用户的参数快照、飞前检查记录与审计日志
 * 一起丢掉，而审计日志在事故复盘时是唯一可信的信息来源。
 */
export class StorageSchemaError extends PersistenceError {
  constructor(
    code: 'version-mismatch' | 'migration-failed',
    message: string,
    advice: string,
    options?: PersistenceErrorOptions,
  ) {
    super(code, message, advice, options);
    this.name = 'StorageSchemaError';
  }
}

const QUOTA_EXCEEDED_NAME = 'QuotaExceededError';
/**
 * 各家的配额错误码：22 是 DOMException 里的 `QUOTA_EXCEEDED_ERR`（Chrome / Edge / Safari），
 * 1014 是 Firefox 的 `NS_ERROR_DOM_QUOTA_REACHED`。只按名字判定会在 Firefox 上漏判。
 */
const QUOTA_EXCEEDED_CODES: readonly number[] = [22, 1014];
const QUOTA_MESSAGE_HINTS: readonly string[] = ['quota', 'QUOTA_REACHED', 'storage full', 'disk full'];

function fieldOf(error: unknown, field: string): unknown {
  if (typeof error !== 'object' || error === null) return undefined;
  return (error as Record<string, unknown>)[field];
}

function messageOf(error: unknown): string {
  const message = fieldOf(error, 'message');
  if (typeof message === 'string') return message;
  return typeof error === 'string' ? error : String(error);
}

export function errorNameOf(error: unknown): string {
  const name = fieldOf(error, 'name');
  return typeof name === 'string' ? name : '';
}

/** 是不是"空间不够"。三重判定见文件头。 */
export function isQuotaExceededError(error: unknown): boolean {
  if (errorNameOf(error) === QUOTA_EXCEEDED_NAME) return true;
  const code = fieldOf(error, 'code');
  if (typeof code === 'number' && QUOTA_EXCEEDED_CODES.includes(code)) return true;
  const message = messageOf(error);
  return QUOTA_MESSAGE_HINTS.some((hint) => message.includes(hint));
}

/** 是不是"这个环境不给用"：隐私模式与禁用站点数据都会在这里出现 */
export function isUnavailableError(error: unknown): boolean {
  const name = errorNameOf(error);
  return name === 'SecurityError' || name === 'UnknownError';
}

/** 打开时版本号比库里已有的低 */
export function isVersionError(error: unknown): boolean {
  return errorNameOf(error) === 'VersionError';
}

/**
 * 造一个与浏览器同形状的配额错误。
 *
 * 内存实现（以及测试里注入的失败存储）必须抛**同形状**的错误，否则"识别配额错误"这段逻辑
 * 在测试里永远走不到真实形态 —— 那样的测试只能证明"我们认识自己编的错误"。
 */
export function createQuotaExceededError(message: string): Error {
  if (typeof DOMException === 'function') return new DOMException(message, QUOTA_EXCEEDED_NAME);
  const error = new Error(message);
  error.name = QUOTA_EXCEEDED_NAME;
  return error;
}

export interface DescribeStorageFailureContext {
  /** 正在做什么，例如 `保存设置`、`写入审计日志`。会出现在提示里 */
  readonly operation: string;
  /** 出错的集合名，用于诊断 */
  readonly collection?: string;
}

/**
 * 把一个**已经归类好**的失败重新包装成错误，便于在内层调用链上继续传播。
 *
 * 场景：`saveLastLink()` 内部要调 `save()`，而 `save()` 已经把配额/不可用归类成了中文失败。
 * 如果在这里把失败降级成一个普通 `Error`，外层再归类时会退化成 `unknown`，
 * 用户看到的原因就从"配额已满，建议清理历史记录"变成"保存失败"。保住分类比重试一次更省事。
 */
export function failureError(failure: StorageFailure): PersistenceError {
  return new PersistenceError(
    failure.code,
    failure.message,
    failure.advice,
    failure.cause === undefined ? undefined : { cause: failure.cause },
  );
}

/** 把任意错误翻译成可读的中文失败描述 */export function describeStorageFailure(
  error: unknown,
  context: DescribeStorageFailureContext,
): StorageFailure {
  if (error instanceof PersistenceError) {
    return {
      code: error.code,
      message: error.message,
      advice: error.advice,
      cause: error,
    };
  }

  if (isQuotaExceededError(error)) {
    return {
      code: 'quota-exceeded',
      message: `${context.operation}失败：浏览器存储配额已满。`,
      advice:
        '在「设置 → 数据与存储」里清理历史审计日志、旧参数快照与旧航线草稿后重试；本次改动未能保存，请不要直接关闭页面。',
      cause: error,
    };
  }

  if (isUnavailableError(error)) {
    return {
      code: 'unavailable',
      message: `${context.operation}失败：浏览器不允许使用持久化存储（可能处于隐私模式，或站点数据被禁用）。`,
      advice: '本次会话的改动不会被保存，刷新或关闭页面即丢失。请退出隐私模式，或允许本站保存站点数据。',
      cause: error,
    };
  }

  if (isVersionError(error)) {
    return {
      code: 'version-mismatch',
      message: `${context.operation}失败：本地数据库里已有比当前程序更新的数据版本。`,
      advice:
        '请升级到较新的 HGC 版本后再打开；不要清除站点数据，那会把已存档的参数快照与审计日志一起删掉。',
      cause: error,
    };
  }

  return {
    code: 'unknown',
    message: `${context.operation}失败：${messageOf(error)}`,
    advice: '请重试；若持续失败，请先在「设置 → 数据与存储」里导出数据，再清理本站点的存储。',
    cause: error,
  };
}
